/**
 * CSV Generation Mixin
 *
 * Provides shared functionality for generating and downloading CSV files.
 * Can be used by components that need to create CSV templates or export data.
 *
 * Usage:
 * import csvGenerationMixin from "@/mixins/csvGenerationMixin";
 *
 * export default {
 *   mixins: [csvGenerationMixin],
 *   // ... rest of component
 * }
 *
 * Methods provided:
 * - generateCSV(columns, data) - Generates CSV content from columns and data
 * - downloadCSV(csvContent, filename) - Downloads CSV content as a file
 * - downloadCSVTemplate(columns, filename) - Downloads a CSV template with headers
 * - escapeCsvValue(value) - Escapes a value for CSV format
 *
 * Data provided:
 * - csvDownloadErrorText - Error message for failed CSV downloads
 */

export default {
  data() {
    return {
      csvDownloadErrorText: "CSV download is not currently available. Please try again later",
    };
  },

  methods: {
    /**
     * Escapes a value for CSV format
     * @param {*} value - The value to escape
     * @returns {string} - The escaped CSV value
     */
    escapeCsvValue(value) {
      if (value === null || value === undefined) return "";

      // Handle object values (extract label, text, name, or value property)
      if (typeof value === "object") {
        value = value.label || value.text || value.name || value.value || value;
      }

      const stringValue = String(value);

      // If value contains comma, quote, or newline, wrap in quotes and escape quotes
      if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }

      return stringValue;
    },

    /**
     * Generates CSV content from columns and data
     * @param {Array} columns - Array of column headers
     * @param {Array} data - Array of data rows (optional)
     * @returns {string} - The CSV content
     */
    generateCSV(columns, data = []) {
      if (!columns || columns.length === 0) {
        throw new Error("No columns provided for CSV generation");
      }

      const csvRows = [];

      // Add header row
      const headerRow = columns.map(this.escapeCsvValue).join(",");
      csvRows.push(headerRow);

      // Add data rows
      data.forEach((row) => {
        if (Array.isArray(row)) {
          // Row is an array
          const csvRow = row.map(this.escapeCsvValue).join(",");
          csvRows.push(csvRow);
        } else if (typeof row === "object") {
          // Row is an object - map columns to values
          const csvRow = columns
            .map((column) => {
              const value =
                typeof column === "object"
                  ? row[column.value || column.key || column]
                  : row[column];
              return this.escapeCsvValue(value);
            })
            .join(",");
          csvRows.push(csvRow);
        }
      });

      return csvRows.join("\n");
    },

    /**
     * Downloads CSV content as a file
     * @param {string} csvContent - The CSV content to download
     * @param {string} filename - The filename for the download (default: 'data.csv')
     */
    downloadCSV(csvContent, filename = "data.csv") {
      try {
        if (!csvContent) {
          throw new Error("No CSV content provided");
        }

        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");

        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", filename);
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } else {
          throw new Error("Browser does not support file downloads");
        }
      } catch (error) {
        console.error("Error downloading CSV:", error);
        this.$store.dispatch("showSnackbar", {
          text: this.csvDownloadErrorText,
          color: "error",
        });
      }
    },

    /**
     * Downloads a CSV template with headers and empty data row
     * @param {Array} columns - Array of column headers
     * @param {string} filename - The filename for the download (default: 'template.csv')
     */
    downloadCSVTemplate(columns, filename = "template.csv") {
      try {
        if (!columns || columns.length === 0) {
          throw new Error("No columns available for template download");
        }

        // Generate CSV with headers and empty data row
        const csvContent = this.generateCSV(columns, [[]]);
        this.downloadCSV(csvContent, filename);
      } catch (error) {
        console.error("Error downloading CSV template:", error);
        this.$store.dispatch("showSnackbar", {
          text: this.csvDownloadErrorText,
          color: "error",
        });
      }
    },
  },
};
